/**
 * VIKKI — deterministic local search + topic inference
 *
 * Phase 1 only. No external API. No private content.
 * Scores each public-page entry against the visitor query and returns
 * the top matches plus an inferred topic for downstream draft/CTA logic.
 */

import { VIKKI_KNOWLEDGE } from '../data/vikkiKnowledge';

const STOPWORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'i', 'me', 'my', 'we', 'our', 'you', 'your', 'they', 'them',
  'and', 'or', 'but', 'so', 'if', 'as', 'at', 'by', 'for', 'from',
  'in', 'on', 'of', 'to', 'with', 'about', 'into', 'than', 'that',
  'this', 'these', 'those', 'it', 'its', 'do', 'does', 'did', 'how',
  'what', 'where', 'when', 'who', 'why', 'whom', 'tell', 'show',
  'me', 'give', 'please', 'can', 'could', 'would', 'should', 'will',
  'have', 'has', 'had', 'just', 'some', 'any', 'more', 'help',
]);

export function tokenize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s/-]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));
}

/**
 * Score a single page entry against tokenized query.
 * Higher score = more relevant.
 */
function scorePage(page, queryTokens, queryLower) {
  let score = 0;

  for (const t of queryTokens) {
    if (page.keywords.includes(t))                                score += 6;
    if (page.title.toLowerCase().includes(t))                      score += 4;
    if (page.description.toLowerCase().includes(t))                score += 2;
    if (page.summary.toLowerCase().includes(t))                    score += 1;
  }

  // Phrase boost from suggested questions
  for (const q of page.questions || []) {
    const ql = q.toLowerCase();
    if (queryLower === ql)                                         score += 20;
    else if (queryLower.length >= 4 && ql.includes(queryLower))    score += 8;
  }

  return score;
}

/**
 * Return top N matching public pages for a visitor query.
 */
export function searchPages(query, limit = 3) {
  const queryLower = String(query || '').toLowerCase().trim();
  const queryTokens = tokenize(queryLower);
  if (queryTokens.length === 0) return [];

  const scored = VIKKI_KNOWLEDGE
    .map((p) => ({ page: p, score: scorePage(p, queryTokens, queryLower) }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scored;
}

/**
 * Map keyword clusters → topic key used by email-draft generator.
 */
const TOPIC_PATTERNS = [
  { topic: 'identity',     match: /\b(are\s+you|who\s+are\s+you|are\s+you\s+(a\s+|an\s+)?(bot|robot|ai|human|llm|gpt)|are\s+you\s+vikrant|chatgpt)\b/i },
  { topic: 'private',      match: /\b(private|internal|confidential|proprietary|classified|cui|export.controlled|secret)\b/i },
  { topic: 'licensing',    match: /\b(licen[cs]e|licen[cs]ing|royalt|partnership|partnerships|monetiz|sbir|sttr|\bota\b|\bbaa\b|cross.?license|joint.?development|jda|acquisition|rofr|right.?of.?first.?refusal|exclusive|non.?exclusive|mutual\s?nda)\b/i },
  { topic: 'contact',      match: /\b(contact|email|phone|reach|call|talk|speak|message|human|person)\b/i },
  { topic: 'patents',      match: /\b(patent|patents|portfolio|uspto|invention|\bip\b)\b/i },
  { topic: 'government',   match: /\b(government|defense|agency|prime|dod|dla|nstxl|sam|uei|cage|naics|federal|public sector|military|gov)\b/i },
  { topic: 'compliance',   match: /\b(compliance|section\s?889|cmmc|far|cybersecurity|safeguarding|cui|fci|audit)\b/i },
  { topic: 'ai-fabric',    match: /\b(ai\s?fabric|ethernet|infiniband|spectrum|uec|roce|gpu|cluster|data\s?center|infrastructure|fabric)\b/i },
  { topic: 'mission',      match: /\b(mission\s?assurance|pcie|pam4|link\s?assurance|signal\s?integrity|interconnect|eye\s?diagram)\b/i },
  { topic: 'demo',         match: /\b(demo|interactive|terrain|calculator|visualization|risk\s?model|try|tool|explorer)\b/i },
  { topic: 'resources',    match: /\b(resource|publication|book|paper|brief|download|pptx|pdf|capability\s?statement|deck)\b/i },
  { topic: 'updates',      match: /\b(news|updates|latest|announcement|recent|new\b)/i },
  { topic: 'about',        match: /\b(about|founder|principal|background|experience|team|leadership)\b/i },
  { topic: 'capability',   match: /\b(capabilit|service|offering|what.*do|how.*help|reliability|supply.*chain|provenance|manufacturing|stability|predictive|failure)\b/i },
  { topic: 'architecture', match: /\b(total\s?solution|architecture|p1.?p5|pillars|scoring|evidence|decision|framework|big\s?picture)\b/i },
];

export function inferTopic(query) {
  const q = String(query || '');
  if (!q.trim()) return 'general';
  for (const { topic, match } of TOPIC_PATTERNS) {
    if (match.test(q)) return topic;
  }
  return 'general';
}

/**
 * Best-fit public-safe answer routing for a given topic.
 * Returns { primary: { path, title }, related: [...] } or null.
 */
export const TOPIC_TO_PRIMARY_PATH = {
  identity:     null,
  private:      '/contact',
  licensing:    '/licensing',
  contact:      '/contact',
  patents:      '/patent-portfolio',
  government:   '/government-defense',
  compliance:   '/compliance',
  'ai-fabric':  '/ai-fabric-assurance',
  mission:      '/mission-assurance',
  demo:         '/salar-resilience-terrain',
  resources:    '/resources',
  updates:      '/news-updates',
  about:        '/about',
  capability:   '/capabilities',
  architecture: '/total-solution-architecture',
};
